import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  CardRow,
  DataTable,
  InfoHint,
  StatCard,
  StatusPill,
  invoiceStatusToPill,
  type DataTableColumn,
} from '../../components/ui';
import { api } from '../../lib/api';
import { formatDate, formatHours, formatMoney, formatPercent } from '../../lib/format';

/** Mirrors `backend/src/modules/support/agreement-prepaid.ts`. */
interface PrepaidInvoiceRow {
  lineId: string;
  invoiceId: string;
  number: string;
  state: string;
  issueDate: string;
  description: string;
  amount: number;
  hours: number | null;
  hoursFrom: 'block' | 'quantity' | null;
  billedWorkHours: number;
}

export interface AgreementPrepaid {
  agreementId: string;
  applies: boolean;
  purchased: number;
  pending: number;
  burnt: number;
  burntSince: string | null;
  available: number;
  availableShare: number | null;
  invoices: PrepaidInvoiceRow[];
  reconciliation: {
    invoicedHours: number;
    purchasedHours: number;
    difference: number;
    status: 'matches' | 'differs' | 'nothing';
  };
  clientBank: { balance: number; overage: number; status: string } | null;
}

export function useAgreementPrepaid(agreementId: string, enabled = true) {
  return useQuery({
    queryKey: ['agreements', 'prepaid', agreementId],
    queryFn: () => api.get<AgreementPrepaid>(`/support/agreements/${agreementId}/prepaid`),
    enabled: enabled && !!agreementId,
  });
}

const h = (n: number) => formatHours(n, { unit: true });

/**
 * PREPAID HOURS — purchased, burnt, available, and the invoices that bought
 * them. Jesse: *"should always show the difference between the total
 * accumulated hours purchased and the amount of hours used and unused."*
 *
 * Renders for a SUPPORT agreement, or any agreement that owns purchased hours
 * (MxD's "2026 VTC Campaign" is a PROJECT in Harvest). Renders nothing
 * otherwise — an empty prepaid card on a fixed-bid build is noise.
 */
export function PrepaidHoursCard({ agreementId, clientId }: { agreementId: string; clientId: string }) {
  const { data, isError } = useAgreementPrepaid(agreementId);
  if (isError || !data || !data.applies) return null;

  // Nothing bought on this agreement (billed in arrears, or not sold yet):
  // "93 h over" would call ordinary billed work an overdraft.
  const nothingBought = data.purchased === 0 && data.pending === 0;
  const over = !nothingBought && data.available < 0;
  const rec = data.reconciliation;

  const columns: Array<DataTableColumn<PrepaidInvoiceRow>> = [
    {
      key: 'number',
      header: 'Invoice',
      render: (r) => (
        <div>
          <Link to={`/invoices/${r.invoiceId}`} className="font-semibold text-secondary hover:underline">
            #{r.number}
          </Link>
          <p className="max-w-[22rem] truncate text-caption text-grey-500" title={r.description}>
            {r.description}
          </p>
        </div>
      ),
    },
    {
      key: 'state',
      header: 'Status',
      render: (r) => <StatusPill status={invoiceStatusToPill(r.state)} label={r.state} />,
    },
    { key: 'date', header: 'Issued', render: (r) => formatDate(r.issueDate) },
    {
      key: 'hours',
      header: 'Hours bought',
      align: 'right',
      render: (r) =>
        r.hours === null ? (
          // Billed in arrears: these hours were worked first and invoiced after,
          // so they were not bought — counting them would buy them twice.
          <span className="text-grey-400">
            {r.billedWorkHours > 0 ? `billed as worked · ${formatHours(r.billedWorkHours, { fixed: true })}` : 'not hours'}
          </span>
        ) : (
          formatHours(r.hours, { fixed: true })
        ),
    },
    { key: 'amount', header: 'Amount', align: 'right', render: (r) => formatMoney(r.amount) },
  ];

  return (
    <div className="space-y-4">
      <CardRow cols={3}>
        <StatCard
          kind="support"
          label="Purchased"
          value={h(data.purchased)}
          hint={data.pending > 0 ? `+ ${h(data.pending)} not active yet` : 'invoiced or paid'}
        />
        <StatCard kind="hours" label="Burnt" value={h(data.burnt)} hint={
            data.burntSince
              ? `billable hours logged since ${formatDate(data.burntSince)}`
              : 'billable hours logged here'
          }
        />
        <StatCard
          kind={over ? 'overage' : 'budget'}
          label={
            <span className="whitespace-nowrap">
              Available{' '}
              <InfoHint label="How available is worked out">
                <p>
                  Paid hours purchased on this agreement, less the billable hours logged to it. Hours on an
                  invoice that has not been paid are shown beside it and not counted until the payment lands.
                </p>
                {data.clientBank && (
                  <p className="mt-2">
                    The client’s support bank — which drives alerts and keeps paid-for hours off new invoices —
                    spends every block the client owns, oldest expiry first. It holds{' '}
                    <span className="num">{h(data.clientBank.balance)}</span>
                    {data.clientBank.overage > 0 ? ` and is ${h(data.clientBank.overage)} over` : ''}.{' '}
                    <Link to={`/clients/${clientId}?tab=support`} className="text-primary hover:underline">
                      Open the bank
                    </Link>
                  </p>
                )}
              </InfoHint>
            </span>
          }
          value={nothingBought ? '—' : over ? `${h(-data.available)} over` : h(data.available)}
          hint={
            nothingBought
              ? 'no hours purchased on this agreement'
              : over
              ? 'used beyond what was bought'
              : data.availableShare !== null
                ? `${formatPercent(data.availableShare)} of the purchase left`
                : 'nothing purchased yet'
          }
        />
      </CardRow>
      <Card>
        <CardHeader
          title="Invoices on this agreement"
          subtitle={
            rec.status === 'nothing'
              ? data.invoices.length === 0
                ? 'No invoice is linked to this agreement yet — link one from the invoice page.'
                : 'None of these invoices bought hours — they bill work already done, or other charges.'
              : rec.status === 'matches'
                ? `${h(rec.invoicedHours)} invoiced · ${h(rec.purchasedHours)} purchased — reconciled`
                : `${h(rec.invoicedHours)} invoiced · ${h(rec.purchasedHours)} purchased — ${h(Math.abs(rec.difference))} ${
                    rec.difference > 0 ? 'invoiced with no hours behind it' : 'purchased with no invoice behind it'
                  }`
          }
          actions={
            rec.status === 'nothing' ? undefined : (
              <StatusPill
                status={rec.status === 'matches' ? 'success' : 'warning'}
                label={rec.status === 'matches' ? 'Reconciled' : 'Differs'}
              />
            )
          }
        />
        {data.invoices.length > 0 && (
          <DataTable columns={columns} data={data.invoices} rowKey={(r) => r.lineId} />
        )}
      </Card>
    </div>
  );
}
